import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Clock, Calendar, Users, Euro, UtensilsCrossed, CheckCircle2, Send, Lock, Flag, Pencil, Trash2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { UserName } from '../components/MessagingContext';
import { JobClosePanel } from '../components/JobClosePanel';
import { categoryById, PER_DIEM_LABELS } from '../data/demo';
import { VerifiedBadge, ProfessionalPublishedBadge, SubstitutionBadge, UrgentBadge, CategoryTag } from '../components/Badges';
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

export const OfertaModal = () => {
  const navigate = useNavigate();
  const close = () => navigate(-1);
  return <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
    <DialogContent data-testid="job-offer-modal" className="w-[calc(100vw_-_24px)] max-w-4xl h-[90dvh] p-0 gap-0 flex flex-col overflow-hidden bg-[#0B0C0E] border-[#343B48] rounded-xl" onCloseAutoFocus={(event) => event.preventDefault()}>
      <div className="shrink-0 border-b border-[#2A2F3D] px-5 py-4 pr-12">
        <DialogTitle>Detalle de la oferta</DialogTitle><DialogDescription className="text-xs mt-1">El listado y tus filtros se mantienen detrás de esta ventana.</DialogDescription>
      </div>
      <div data-testid="job-offer-modal-scroll" className="overflow-y-auto min-h-0 flex-1"><OfertaDetailPage modal /></div>
    </DialogContent>
  </Dialog>;
};


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfertaDetailPage({ modal = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [state, setState] = useState('loading');
  const [applied, setApplied] = useState(null);
  const [sending, setSending] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [closed, setClosed] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setState('loading');
    fetch(`${API}/jobs/${id}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => { setJob(j); setClosed(j.status && j.status !== 'live'); setState('ready'); })
      .catch(() => setState('notfound'));
  }, [id]);

  useEffect(() => {
    if (!user || !job) return;
    fetch(`${API}/applications/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setApplied(list.find((a) => a.job_id === job.id) || null))
      .catch(() => {});
  }, [user, job]);

  if (state === 'loading') return (
    <div className={`${modal ? '' : 'min-h-screen'} bg-[#0B0C0E]`}>{!modal && <Navbar />}
      <div className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-500 font-mono text-sm" data-testid="job-loading">Cargando…</div>{!modal && <Footer />}</div>
  );
  if (state === 'notfound' || !job) return (
    <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">Oferta no encontrada</h1>
        <button onClick={() => navigate('/ofertas')} className="text-amber-500 font-mono uppercase tracking-wider text-sm">← Ver todas las ofertas</button>
      </div><Footer /></div>
  );
  const cat = categoryById(job.category);
  const isOwner = user && job.owner_user_id === user.user_id;
  const isCompany = job.author?.type === 'company';
  const fmt = (iso) => new Date(iso).toLocaleDateString(locale === 'ca' ? 'ca-ES' : locale === 'en' ? 'en-GB' : 'es-ES', { day: '2-digit', month: 'long', year: 'numeric' });

  const updateApplication = (application, applicants) => {
    setApplied(application);
    setJob((j) => ({ ...j, applicants }));
    window.dispatchEvent(new CustomEvent('stagecrew:job-applicants', { detail: { jobId: job.id, applicants } }));
  };

  const apply = async () => {
    if (!user) { navigate('/login'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/jobs/${job.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ job_title: job.title, company_name: job.author.name, company_id: job.author.id, category: job.category }),
      });
      if (res.status === 403) { const d = await res.json(); toast.error(d.detail?.message || 'Acción restringida'); return; }
      if (res.status === 409) {
        const data = await res.json();
        if (data.application) {
          updateApplication(data.application, data.applicants);
          toast.info('Ya habías enviado tu candidatura a esta oferta.');
        } else { setClosed(true); toast.info(data.detail || 'La oferta ya está cerrada'); }
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      updateApplication(data.application, data.applicants);
      toast.success(`Candidatura enviada a ${job.author.name}`);
    } catch {
      toast.error('No se pudo enviar la candidatura. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const withdraw = async () => {
    if (withdrawing) return;
    setWithdrawing(true);
    try {
      const res = await fetch(`${API}/jobs/${job.id}/application`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409) setClosed(true);
        toast.error(typeof data.detail === 'string' ? data.detail : t('application.withdrawError'));
        return;
      }
      updateApplication(null, data.applicants);
      toast.success(t('application.withdrawSuccess'));
    } catch { toast.error(t('application.withdrawError')); }
    finally { setWithdrawing(false); }
  };

  const completeJob = async () => {
    setCompleting(true);
    try {
      const res = await fetch(`${API}/jobs/${job.id}/complete`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'No se pudo finalizar'); return; }
      toast.success('Trabajo finalizado. Ambas partes deben valorar en 7 días.');
      setJob((j) => ({ ...j, status: 'completed' }));
    } catch {
      toast.error('No se pudo finalizar el trabajo.');
    } finally { setCompleting(false); }
  };

  const deleteJob = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/jobs/${job.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof data.detail === 'string' ? data.detail : 'No se pudo eliminar la oferta'); return; }
      toast.success('Oferta eliminada.');
      setConfirmDelete(false);
      setTimeout(() => navigate('/ofertas'), 500);
    } catch { toast.error('No se pudo eliminar la oferta.'); }
    finally { setDeleting(false); }
  };

  return (
    <div className={`${modal ? '' : 'min-h-screen'} bg-[#0B0C0E]`}>
      {!modal && <Navbar />}
      <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${modal ? 'pt-6 pb-8' : 'pt-8 pb-16'}`}>
        {!modal && <Link to="/ofertas" data-testid="back-to-jobs" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-8">
          <ArrowLeft size={14} /> Ver todas las ofertas
        </Link>}

        <div className="flex flex-wrap items-center gap-2 mb-5">
          {cat && <CategoryTag name={cat.name} accent={cat.accent} />}
          {job.substitution && <SubstitutionBadge label={`🔄 ${t('badge.substitution')}`} />}
          {job.urgent && !closed && <UrgentBadge label={t('badge.urgent')} />}
          {closed && <span data-testid="job-closed-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border font-mono tracking-wide uppercase text-slate-300 bg-slate-500/15 border-slate-500/50"><Lock size={11} /> {job.status === 'completed' ? 'Finalizada' : 'Oferta completa · cerrada'}</span>}
        </div>

        <h1 data-testid="job-offer-title" className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{job.title}</h1>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <UserName userId={job.author.id} testId="job-author-name" className="text-lg font-medium text-slate-200 [overflow-wrap:anywhere]">{job.author.name}</UserName>
          {job.author.type === 'company' && job.author.verified && <VerifiedBadge label={t('badge.verified')} />}
          {job.author.type === 'professional' && <ProfessionalPublishedBadge label={t('badge.professionalPublished')} />}
        </div>

        {isOwner && job.status !== 'completed' && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid="job-owner-actions">
            {!closed && (
              <Link to={`/publicar-oferta?edit=${job.id}`} data-testid="job-edit-btn" className="inline-flex items-center justify-center gap-2 border border-[#2A2F3D] hover:border-amber-500/60 text-slate-200 font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <Pencil size={15} /> Editar oferta
              </Link>
            )}
            <button type="button" data-testid="job-delete-btn" onClick={() => setConfirmDelete(true)} className="inline-flex items-center justify-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 font-semibold px-5 py-2.5 rounded-lg transition-colors">
              <Trash2 size={15} /> Eliminar oferta
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <InfoCell icon={MapPin} label={t('label.location')} value={job.location} />
          <InfoCell icon={Clock} label={t('label.schedule')} value={job.schedule} />
          <InfoCell icon={Euro} label={t('label.compensation')} value={job.compensationRaw} accent />
          <InfoCell icon={UtensilsCrossed} label="Dietas" value={PER_DIEM_LABELS[job.perDiem || 'none']} sub={job.perDiemDetail} testId="job-perdiem" green={job.perDiem && job.perDiem !== 'none'} />
          <InfoCell icon={Calendar} label={t('label.deadline')} value={fmt(job.deadline)} />
        </div>

        {(job.timeSlots?.length || job.totalHours) && (
          <div className="sc-card p-8 mb-8" data-testid="job-timeslots">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <h3 className="sc-mono-eyebrow text-amber-500">{t('label.timeSlots')}</h3>
              {job.totalHours != null && (
                <div className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/5 text-amber-500 px-3 py-1.5 rounded-lg" data-testid="job-total-hours">
                  <Clock size={13} />
                  <span className="text-xs font-mono uppercase tracking-wider">{t('label.totalHours')}</span>
                  <span className="font-mono font-semibold">{job.totalHours} {t('label.hoursShort')}</span>
                </div>
              )}
            </div>
            <div className="divide-y divide-[#2A2F3D]">
              {(job.timeSlots || []).map((slot, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-3 text-sm">
                  <div className="text-slate-300 font-medium">{slot.day}</div>
                  <div className="text-slate-400 font-mono text-xs uppercase tracking-wider sm:col-span-2">
                    {slot.from} <span className="text-slate-600">→</span> {slot.to}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sc-card p-8 mb-8">
          <h3 className="sc-mono-eyebrow text-amber-500 mb-4">Descripción</h3>
          <p className="text-slate-300 leading-relaxed text-base">{job.description}</p>
        </div>

        <div className="sc-card p-8 mb-8">
          <h3 className="sc-mono-eyebrow text-amber-500 mb-4">{t('label.skills')}</h3>
          <div className="flex flex-wrap gap-2">
            {job.skills.map((s) => (
              <span key={s} className="text-sm font-mono px-3 py-1.5 rounded-md border border-[#2A2F3D] text-slate-300">{s}</span>
            ))}
          </div>
        </div>

        {isOwner && <JobClosePanel job={job} closed={closed} onClosed={() => { setClosed(true); }} />}

        {isOwner && closed && job.status !== 'completed' && (
          <div className="sc-card p-6 md:p-8 mb-8 border-amber-500/30" data-testid="job-complete-panel">
            <h3 className="sc-mono-eyebrow text-amber-500 mb-3">Finalizar el trabajo</h3>
            <p className="text-sm text-slate-400 mb-5 leading-relaxed">Cuando el trabajo se haya realizado, márcalo como finalizado. Se pedirá a ambas partes una valoración obligatoria (1-5 estrellas + comentario) en un plazo de 7 días. Quien no valore no podrá coger ni publicar ofertas durante 1 semana.</p>
            <button data-testid="job-complete-btn" onClick={completeJob} disabled={completing} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
              <Flag size={15} /> {completing ? 'Finalizando…' : 'Marcar trabajo como finalizado'}
            </button>
          </div>
        )}
        {job.status === 'completed' && (
          <div className="sc-card p-6 mb-8 border-emerald-500/30 flex items-center gap-3" data-testid="job-completed-banner">
            <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
            <span className="text-sm text-emerald-300">Trabajo finalizado. Las valoraciones entre las partes están en curso.</span>
          </div>
        )}

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-8 border-t border-[#2A2F3D]">
          <div data-testid="job-applicants-count" className="text-sm font-mono uppercase tracking-wider text-slate-500">
            <Users size={13} className="inline mr-1" /> {job.applicants} {t('label.applicants')}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {isCompany && !isOwner && (
              <Link to={`/empresas/${job.author.id}`} data-testid="view-company-link" className="text-sm text-center text-slate-300 border border-[#2A2F3D] hover:border-amber-500/50 px-5 py-3 rounded-lg transition-colors">
                {t('label.viewCompany')}
              </Link>
            )}
            {isOwner ? null : closed && !applied ? (
              <div data-testid="apply-closed" className="inline-flex items-center justify-center gap-2 border border-slate-500/50 bg-slate-500/10 text-slate-300 font-semibold px-6 py-3 rounded-lg"><Lock size={15} /> Oferta completa · ya no admite candidaturas</div>
            ) : applied ? (
              <div data-testid="apply-sent">
                {closed ? <div className="inline-flex items-center justify-center gap-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-400 font-semibold px-6 py-3 rounded-lg">
                  <CheckCircle2 size={16} />
                  Candidatura enviada · {applied.status === 'accepted' ? 'Aceptada' : applied.status === 'rejected' ? 'No seleccionada' : 'Pendiente'}
                </div> : <button type="button" data-testid="apply-withdraw-btn" onClick={withdraw} disabled={withdrawing} className="w-full inline-flex items-center justify-center gap-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-400 hover:border-red-400/60 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-60 font-semibold px-6 py-3 rounded-lg transition-colors">
                  <CheckCircle2 size={16} className="shrink-0" /><span>{t('application.sent')}<span className="block text-xs font-normal mt-0.5">{t(withdrawing ? 'application.withdrawing' : 'application.withdraw')}</span></span>
                </button>}
              </div>
            ) : (
              <button data-testid="apply-btn" onClick={apply} disabled={sending} className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
                <Send size={15} />
                {sending ? 'Enviando…' : user ? 'Enviar candidatura →' : 'Entrar para enviar candidatura'}
              </button>
            )}
          </div>
        </div>
        {applied && (
          <p className="text-xs text-slate-500 mt-3 md:text-right" data-testid="apply-sent-hint">
            {t(closed ? 'application.closedHint' : 'application.withdrawHint')} Sigue su estado en <Link to="/mi-perfil?tab=candidaturas" data-testid="application-my-profile" className="text-amber-500 hover:underline">Mi perfil</Link>.
          </p>
        )}
      </div>
      {!modal && <Footer />}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="job-delete-dialog" className="w-[calc(100%-2rem)] max-w-md bg-[#121418] border-[#2A2F3D] rounded-xl">
          <DialogTitle>Eliminar oferta</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">Esta acción no se puede deshacer. Se eliminará la oferta «{job.title}» y sus candidaturas dejarán de estar asociadas.</DialogDescription>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button type="button" data-testid="job-delete-cancel" onClick={() => setConfirmDelete(false)} className="flex-1 border border-[#2A2F3D] hover:border-amber-500/40 text-slate-200 font-semibold px-5 py-2.5 rounded-lg transition-colors">Cancelar</button>
            <button type="button" data-testid="job-delete-confirm" onClick={deleteJob} disabled={deleting} className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"><Trash2 size={15} /> {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const InfoCell = ({ icon: Icon, label, value, sub, accent, green, testId }) => (
  <div className="sc-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500 mb-2">
      <Icon size={14} />
      <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
    </div>
    <div className={`text-sm font-medium ${accent ? 'text-amber-500 font-mono' : green ? 'text-emerald-400' : 'text-white'}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{sub}</div>}
  </div>
);
